import { useState } from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { authAPI } from '../services/api';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);
        setError('');
        setMessage('');

        try {
            const response = await authAPI.requestPasswordReset(email);
            setMessage(response.data?.message || 'If an account exists for that email, password reset instructions have been sent.');
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Unable to request a password reset right now. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050509] flex items-center justify-center p-4 relative overflow-hidden">
            <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="w-full max-w-md bg-[#0B0B10] border border-white/10 p-8 rounded-2xl shadow-2xl relative z-10">
                <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8">
                    <ArrowLeftIcon className="w-4 h-4" /> Back to sign in
                </Link>
                <div className="mb-7">
                    <h1 className="text-2xl font-bold text-white mb-2">Forgot your password?</h1>
                    <p className="text-slate-400 text-sm">Enter the email address for your Ubiq account. If an account exists, we'll send a secure reset link.</p>
                </div>
                {message && <div role="status" className="mb-4 p-3 rounded-lg border border-emerald-500/20 bg-emerald-500/10 text-emerald-300 text-sm">{message}</div>}
                {error && <div role="alert" className="mb-4 p-3 rounded-lg border border-red-500/20 bg-red-500/10 text-red-400 text-sm">{error}</div>}
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-1">
                        <label htmlFor="email" className="text-xs font-medium text-slate-400 ml-1">Email address</label>
                        <div className="relative">
                            <EnvelopeIcon className="w-5 h-5 absolute left-3 top-2.5 text-slate-500" />
                            <input
                                id="email"
                                type="email"
                                autoComplete="email"
                                required
                                maxLength={191}
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                className="w-full bg-[#15151A] border border-white/10 rounded-lg py-2.5 pl-10 pr-4 text-slate-200 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 placeholder:text-slate-600"
                                placeholder="name@company.com"
                            />
                        </div>
                    </div>
                    <button type="submit" disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-2.5 rounded-lg transition-all disabled:opacity-70 disabled:cursor-not-allowed">
                        {loading ? 'Sending request…' : 'Send reset link'}
                    </button>
                </form>
            </div>
        </div>
    );
}
